import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Grid2,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState, useRef } from "react";
import { Order, OrderItem } from "../../../orders/utils/types";
import { InvoiceItem } from "../invoiceTypes";
import { Invoice } from "./Invoice";

type Props = {
  open: boolean;
  onClose: () => void;
  order: Order;
};

export const InvoicePreviewModal = ({ open, onClose, order }: Props) => {
  const printRef = useRef<HTMLDivElement>(null);

  const currency =
    order?.deliveryOrder?.amazonOrder?.orderTotalCurrency || "USD";
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [fromName, setFromName] = useState<string>("Your Company Name");
  const [fromAddress, setFromAddress] = useState<string>(
    "Company Address Line 1\nCity, State, Postal Code\nCountry",
  );
  const [toName, setToName] = useState<string>("");
  const [toAddress, setToAddress] = useState<string>("");
  const [items, setItems] = useState<InvoiceItem[]>([]);

  useEffect(() => {
    if (!order) return;
    const invNo =
      order.deliveryOrder?.amazonOrder?.sellerOrderId ||
      order.amazonOrderId ||
      order.barcode ||
      "-";
    setInvoiceNumber(invNo);

    const defaultDate = new Date().toISOString().slice(0, 10);
    const dateFromOrder = order.deliveryOrder?.amazonOrder?.purchaseDate
      ? new Date(order.deliveryOrder.amazonOrder.purchaseDate)
          .toISOString()
          .slice(0, 10)
      : defaultDate;
    setInvoiceDate(dateFromOrder);

    const s = order.deliveryOrder?.shippingAddress;
    const name = s?.name || order.deliveryOrder?.buyerInfo?.buyerName || "";
    setToName(name);
    const addrParts = [
      s?.addressLine1,
      s?.addressLine2,
      s?.addressLine3,
      s?.city,
      s?.stateOrRegion,
      s?.postalCode,
      s?.countryCode,
    ].filter(Boolean);
    setToAddress(addrParts.join("\n"));

    const mapped: InvoiceItem[] = (order.orderItems || []).map(
      (oi: OrderItem) => {
        const rawQuantity = oi.quantityOrdered ?? 1;
        const quantity = rawQuantity > 0 ? rawQuantity : 1;
        const lineTotal = Number(oi.itemPrice ?? 0);
        const divisor = quantity > 0 ? quantity : 1;
        return {
          id: oi.id,
          description:
            oi.productDetails?.marketplaceProduct?.itemName ?? oi.title,
          quantity,
          unitPrice: lineTotal / divisor,
        };
      },
    );
    setItems(mapped);
  }, [order]);

  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (acc, it) => acc + Number(it.quantity || 0) * Number(it.unitPrice || 0),
      0,
    );
    return { subtotal };
  }, [items]);

  const handleItemChange = (
    id: number,
    key: keyof Pick<InvoiceItem, "description" | "quantity" | "unitPrice">,
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              [key]: key === "description" ? value : Number(value || 0),
            }
          : it,
      ),
    );
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>INVOICE - ${invoiceNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              padding: 40px 50px;
              color: #000;
              background: #fff;
            }
            .invoice-container {
              max-width: 850px;
              margin: 0 auto;
            }
            .document-title {
              text-align: center;
              font-size: 32px;
              font-weight: bold;
              letter-spacing: 4px;
              margin-bottom: 10px;
              text-transform: uppercase;
              border-top: 3px solid #000;
              border-bottom: 3px solid #000;
              padding: 15px 0;
            }
            .invoice-meta {
              display: flex;
              justify-content: space-between;
              margin: 30px 0;
              padding: 20px;
              background: #f8f8f8;
              border: 1px solid #333;
            }
            .meta-item {
              display: flex;
              flex-direction: column;
            }
            .meta-label {
              font-size: 11px;
              font-weight: bold;
              color: #555;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            .meta-value {
              font-size: 16px;
              font-weight: normal;
              color: #000;
            }
            .parties-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin: 40px 0;
            }
            .party-box {
              border: 2px solid #000;
              padding: 20px;
              min-height: 150px;
            }
            .party-header {
              font-size: 11px;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              margin-bottom: 15px;
              padding-bottom: 8px;
              border-bottom: 1px solid #666;
            }
            .party-name {
              font-weight: bold;
              font-size: 15px;
              margin-bottom: 10px;
            }
            .party-address {
              font-size: 13px;
              line-height: 1.7;
              white-space: pre-line;
              color: #222;
            }
            .items-section {
              margin: 40px 0;
            }
            .items-title {
              font-size: 11px;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              margin-bottom: 15px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
            }
            th {
              background: #000;
              color: #fff;
              padding: 12px 10px;
              text-align: left;
              font-size: 11px;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 1px;
              border: 1px solid #000;
            }
            td {
              padding: 12px 10px;
              border: 1px solid #333;
              font-size: 13px;
              vertical-align: top;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            tbody tr:nth-child(even) {
              background: #fafafa;
            }
            .subtotal-row td {
              font-weight: normal;
              padding-top: 15px;
              border-bottom: 1px solid #666;
            }
            .total-row {
              background: #e8e8e8 !important;
              font-weight: bold;
            }
            .total-row td {
              padding: 15px 10px;
              font-size: 15px;
              border: 2px solid #000;
            }
            .footer-notes {
              margin-top: 50px;
              padding-top: 20px;
              border-top: 1px solid #999;
              font-size: 11px;
              color: #555;
              line-height: 1.6;
            }
            .signature-section {
              margin-top: 60px;
              display: flex;
              justify-content: space-between;
            }
            .signature-box {
              width: 45%;
              text-align: center;
            }
            .signature-line {
              border-top: 2px solid #000;
              margin-top: 50px;
              padding-top: 10px;
              font-size: 12px;
              font-weight: bold;
            }
            @media print {
              body {
                padding: 20px;
              }
              .invoice-container {
                max-width: 100%;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Modal open={open} onClose={onClose} keepMounted>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          maxWidth: "95vw",
          maxHeight: "95vh",
          overflow: "auto",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="h6">Invoice Preview</Typography>
          <Box flex={1} />
          <Button variant="contained" onClick={handlePrint} sx={{ mr: 1 }}>
            Print Invoice
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Screen view (editable) */}
        <Box>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12 }}>
              <Card
                elevation={0}
                sx={{ p: 2, background: (t) => t.palette.customBackground.box }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  className="invoice-header"
                >
                  <TextField
                    label="Invoice Number"
                    size="small"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    sx={{ width: { xs: "100%", md: 320 } }}
                  />
                  <TextField
                    label="Invoice Date"
                    size="small"
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    sx={{ width: { xs: "100%", md: 220 } }}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                </Stack>
              </Card>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{ p: 2, background: (t) => t.palette.customBackground.box }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  From (Seller)
                </Typography>
                <TextField
                  label="Company Name"
                  size="small"
                  fullWidth
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Address"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={fromAddress}
                  onChange={(e) => setFromAddress(e.target.value)}
                />
              </Card>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{ p: 2, background: (t) => t.palette.customBackground.box }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  To (Customer)
                </Typography>
                <TextField
                  label="Customer Name"
                  size="small"
                  fullWidth
                  value={toName}
                  onChange={(e) => setToName(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Shipping Address"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={toAddress}
                  onChange={(e) => setToAddress(e.target.value)}
                />
              </Card>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Card
                elevation={0}
                sx={{ p: 2, background: (t) => t.palette.customBackground.box }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Items
                </Typography>
                <Table size="small" className="invoice-table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "60%" }}>Description</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        Quantity
                      </TableCell>
                      <TableCell align="right" sx={{ width: 160 }}>
                        Price ({currency})
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it) => {
                      const lineTotal =
                        Number(it.quantity || 0) * Number(it.unitPrice || 0);
                      return (
                        <TableRow key={it.id} hover>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={it.description}
                              onChange={(e) =>
                                handleItemChange(
                                  it.id,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  step: 1,
                                },
                              }}
                              value={it.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  it.id,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>

                          <TableCell align="right">
                            {lineTotal.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2} align="right">
                        <Typography fontWeight={600}>
                          TOTAL ({currency})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {totals.subtotal.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </Grid2>
          </Grid2>
        </Box>

        {/* Hidden print content */}
        <Box ref={printRef}>
          <Invoice
            invoiceNumber={invoiceNumber}
            invoiceDate={invoiceDate}
            fromName={fromName}
            fromAddress={fromAddress}
            toName={toName}
            toAddress={toAddress}
            items={items}
            currency={currency}
            subtotal={totals.subtotal}
          />
        </Box>
      </Box>
    </Modal>
  );
};
